import React, { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Award } from "lucide-react";
import { Arma } from "@/lib/armaApi";
import { useInstance } from "@/context/InstanceContext";
import CornerFrame from "@/components/CornerFrame";
import HeroBanner from "@/components/HeroBanner";

const RANGES = [
  { id: "session", label: "SESSION" },
  { id: "today", label: "24H" },
  { id: "7d", label: "7D" },
  { id: "all", label: "ALL TIME" },
];

const COLUMNS = [
  { key: "playerName", label: "Operator", align: "left" },
  { key: "faction", label: "Unit", align: "left" },
  { key: "pvpKills", label: "PvP", align: "right" },
  { key: "aiKills", label: "AI", align: "right" },
  { key: "totalKills", label: "TOTAL", align: "right" },
  { key: "deaths", label: "KIA", align: "right" },
  { key: "totalKd", label: "K/D", align: "right" },
  { key: "friendlyFires", label: "FF", align: "right" },
  { key: "suicides", label: "SUI", align: "right" },
  { key: "vehiclesDestroyed", label: "VEH", align: "right" },
  { key: "avgKillDistance", label: "Ø RANGE", align: "right" },
  { key: "playtimeSeconds", label: "DUTY", align: "right" },
];

function factionPatchCls(f) {
  return f === "US" ? "patch-us" : f === "USSR" ? "patch-ussr" : f === "FIA" ? "patch-fia" : "patch-muted";
}

function formatPlaytime(seconds) {
  if (!seconds) return "0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function RankBadge({ rank }) {
  if (rank > 3) {
    return <span className="font-mono-tech text-xs" style={{ color: "var(--text-faint)" }}>{rank}</span>;
  }
  const colors = ["var(--amber-bright)", "#c0c0c0", "#cd7f32"];
  return (
    <span className="inline-flex items-center justify-center w-6 h-6" style={{
      color: colors[rank - 1],
      border: `1px solid ${colors[rank - 1]}`,
      background: "rgba(0,0,0,0.4)",
    }}>
      <Award className="w-3.5 h-3.5" />
    </span>
  );
}

export default function Scoreboard() {
  const { instance } = useInstance();
  const [range, setRange] = useState("all");
  const [data, setData] = useState({ rows: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState("totalKills");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Arma.scoreboard(instance, range)
      .then((d) => !cancelled && (setData(d), setError(null)))
      .catch((e) => !cancelled && setError(e?.message || "Fehler"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [instance, range]);

  const sorted = useMemo(() => {
    const rows = (data.rows || []).map(r => ({
      ...r,
      totalKills: r.pvpKills + r.aiKills,
      totalKd: r.deaths > 0 ? Math.round((r.pvpKills + r.aiKills) / r.deaths * 100) / 100 : r.pvpKills + r.aiKills,
    }));
    rows.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const an = Number(av) || 0; const bn = Number(bv) || 0;
      return sortDir === "asc" ? an - bn : bn - an;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "playerName" || key === "faction" ? "asc" : "desc");
    }
  };

  return (
    <div className="space-y-5 relative z-[2]">
      <HeroBanner
        eyebrow="UNIT ROSTER · PERFORMANCE INDEX"
        title="SCOREBOARD"
        meta={<>AGGREGATED BY UID · {sorted.length} OPERATORS LOGGED · INSTANCE <span style={{ color: "var(--tan-bright)" }}>{instance}</span></>}
        right={
          <div className="flex border" style={{ borderColor: "var(--border-bright)" }}>
            {RANGES.map((r) => (
              <button
                key={r.id}
                onClick={() => setRange(r.id)}
                className="px-4 py-2 text-xs font-display tracking-widest border-r last:border-r-0 transition"
                style={{
                  background: range === r.id ? "var(--tan-bright)" : "transparent",
                  color: range === r.id ? "#07060a" : "var(--text-muted)",
                  borderColor: "var(--border-bright)",
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      {error && (
        <div className="tac-panel p-3 text-xs font-mono-tech" style={{ borderColor: "var(--red)", color: "var(--red-bright)" }}>
          {error}
        </div>
      )}

      <CornerFrame className="tac-panel">
        <div className="tac-panel-header">
          <div className="label-tech" style={{ color: "var(--tan)" }}>ROSTER</div>
          <div className="label-tech" style={{ color: "var(--text-muted)" }}>{loading ? "SYNC …" : "READY"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: "rgba(0,0,0,0.25)" }}>
              <tr>
                <th className="px-3 py-2 text-left label-tech w-12">RNK</th>
                {COLUMNS.map((c) => {
                  const active = sortKey === c.key;
                  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
                  return (
                    <th
                      key={c.key}
                      onClick={() => toggleSort(c.key)}
                      className={`px-3 py-2 cursor-pointer select-none label-tech hover:text-[var(--tan-bright)] ${
                        c.align === "right" ? "text-right" : "text-left"
                      }`}
                      style={{ color: active ? "var(--tan-bright)" : "var(--text-faint)" }}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        <Icon className="w-3 h-3" />
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={COLUMNS.length + 1} className="px-3 py-8 text-center font-mono-tech text-xs" style={{ color: "var(--text-faint)" }}>SYNCHRONISING ROSTER …</td></tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr><td colSpan={COLUMNS.length + 1} className="px-3 py-8 text-center font-mono-tech text-xs" style={{ color: "var(--text-faint)" }}>NO DATA FOR SELECTED TIMEFRAME</td></tr>
              )}
              {!loading && sorted.map((row, i) => (
                <tr
                  key={row.uid}
                  className="hover:bg-[var(--surface-2)]"
                  style={{ borderTop: "1px solid var(--border-faint)" }}
                >
                  <td className="px-3 py-2"><RankBadge rank={i + 1} /></td>
                  <td className="px-3 py-2 font-display tracking-wide">{row.playerName}</td>
                  <td className="px-3 py-2">
                    <span className={`patch ${factionPatchCls(row.faction)}`}>{row.faction || "—"}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono-tech">{row.pvpKills}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono-tech" style={{ color: "var(--olive-bright)" }}>{row.aiKills}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono-tech font-bold" style={{ color: "var(--tan-bright)" }}>{row.totalKills}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono-tech" style={{ color: "var(--text-muted)" }}>{row.deaths}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono-tech" style={{ color: row.totalKd > 1 ? "var(--olive-bright)" : row.totalKd < 1 ? "var(--red-bright)" : "var(--amber-bright)" }}>{row.totalKd.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono-tech" style={{ color: row.friendlyFires > 0 ? "var(--red-bright)" : "var(--text-faint)" }}>{row.friendlyFires}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono-tech" style={{ color: row.suicides > 0 ? "var(--red-bright)" : "var(--text-faint)" }}>{row.suicides}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono-tech">{row.vehiclesDestroyed}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono-tech" style={{ color: "var(--text-muted)" }}>{row.avgKillDistance}m</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono-tech" style={{ color: "var(--text-muted)" }}>{formatPlaytime(row.playtimeSeconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CornerFrame>
    </div>
  );
}
